import { useState, useTransition } from "react"
import { useDebounceCallback } from "usehooks-ts"

import { useToast } from "./use-toast"

export interface ApiResponse<T = undefined, M = undefined> {
  success: boolean
  errors: string[]
  data?: T
  metadata?: M
}

export const useApiAction = <T, R = undefined, M = undefined>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  {
    debounceTime = 2000,
    showToast = true,
    headers = {},
  }: {
    debounceTime?: number
    showToast?: boolean
    headers?: Record<string, string>
  } = {}
) => {
  const { toast } = useToast()
  const [, startTransition] = useTransition()

  const [response, setResponse] = useState<ApiResponse<R, M> | null>(null)
  const [isPending, setIsPending] = useState(false)

  const handleApiAction = async (data?: T): Promise<ApiResponse<R, M>> => {
    const config: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }

    if (data && method !== "GET") {
      config.body = JSON.stringify(data)
    }

    try {
      const res = await fetch(endpoint, config)
      const responseData = (await res.json()) as ApiResponse<R, M>

      if (showToast && !responseData.success && responseData.errors.length > 0) {
        toast({
          title: responseData.errors[0],
          variant: "destructive",
        })
      }

      return responseData
    } catch (error) {
      const errorResponse: ApiResponse<R, M> = {
        success: false,
        errors: ["Network error occurred"],
      }

      if (showToast) {
        toast({
          title: "Network Error",
          description: "Failed to connect to the server",
          variant: "destructive",
        })
      }

      return errorResponse
    }
  }

  const apiActionSync = (data?: T, callback?: (response: ApiResponse<R, M>) => void) => {
    setIsPending(true)
    startTransition(async () => {
      const _response = await handleApiAction(data)
      setIsPending(false)
      setResponse(_response)
      if (callback && _response) callback(_response)
    })
  }

  const apiAction = (data?: T) => {
    setIsPending(true)
    startTransition(async () => {
      const _response = await handleApiAction(data)
      setIsPending(false)
      setResponse(_response)
    })
  }

  const debouncedApiAction = useDebounceCallback(apiAction, debounceTime)

  const reset = () => {
    setResponse(null)
    setIsPending(false)
  }

  return {
    response,
    apiAction,
    debouncedApiAction,
    isPending,
    apiActionSync,
    reset,
  }
}

// Helper function to create API actions for dynamic endpoints
export const createApiAction = <T, R = undefined, M = undefined>(
  baseEndpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  {
    showToast = true,
    headers = {},
  }: {
    showToast?: boolean
    headers?: Record<string, string>
  } = {}
) => {
  return (endpoint: string, data?: T): Promise<ApiResponse<R, M>> => {
    const config: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }

    if (data && method !== "GET") {
      config.body = JSON.stringify(data)
    }

    return fetch(`${baseEndpoint}${endpoint}`, config)
      .then((res) => res.json())
      .catch(() => ({
        success: false,
        errors: ["Network error occurred"],
      }))
  }
}
